import * as Icons from '@ant-design/icons';
import { Button, Card, DatePicker, Flex, Form, Typography } from 'antd';
import { FieldsetTitle } from 'components/professor_area/FieldsetTitle';
import dayjs from 'dayjs';
import { useCallback, useState } from 'react';
import useNotification from 'hooks/useNotification';
import { useUpdate, useCreate } from '@refinedev/core';

interface PrintControlSectionProps {
  certificate: any;
  form: any;
}

export const PrintControlSection = ({ certificate, form }: PrintControlSectionProps) => {
  const { showError } = useNotification();
  const [isEditingSentDate, setIsEditingSentDate] = useState(false);
  const [isEditingReceivedDate, setIsEditingReceivedDate] = useState(false);
  const { mutateAsync: updateShipment, isLoading: isUpdatingShipment } = useUpdate();
  const { mutateAsync: createShipment, isLoading: isCreatingShipment } = useCreate();

  // Função para desabilitar datas futuras
  const disabledDate = (current: dayjs.Dayjs) => current && current.isAfter(dayjs(), 'day');

  const handleShipmentAction = useCallback(async (
    fieldName: 'sent_by_regulator_at' | 'received_by_secretary_at',
    isEditing: boolean,
    setEditing: (editing: boolean) => void
  ) => {
    if (!isEditing) {
      setEditing(true);
    } else {
      try {
        const values = await form.validateFields([fieldName]);
        
        if (!values[fieldName]) {
          showError(`Data do registro é obrigatória`);
          return;
        }
        
        const shipmentExists = certificate?.last_shipment?.id;
        const formattedDate = values[fieldName].format('YYYY-MM-DD');

        if (shipmentExists) {
          await updateShipment({
            resource: 'certificate_shipments',
            id: certificate.last_shipment.id,
            values: {
              [fieldName]: formattedDate
            },
            successNotification: () => ({
              type: 'success',
              description: 'Sucesso!',
              message: 'Registro salvo com sucesso!'
            }),
            errorNotification: () => ({
              type: 'error',
              description: 'Erro!',
              message: 'Erro ao salvar registro. Tente novamente.'
            })
          });
        } else {
          if (!certificate?.id) {
            showError('ID do certificado não encontrado');
            return;
          }

          await createShipment({
            resource: 'certificate_shipments',
            values: {
              graduate_course_certificate_id: certificate.id,
              [fieldName]: formattedDate
            },
            successNotification: () => ({
              type: 'success',
              description: 'Sucesso!',
              message: 'Registro salvo com sucesso!'
            }),
            errorNotification: () => ({
              type: 'error',
              description: 'Erro!',
              message: 'Erro ao salvar registro. Tente novamente.'
            })
          });
        }

        setEditing(false);
      } catch (error) {
        console.error('Erro ao salvar registro:', error);
      }
    }
  }, [form, certificate?.last_shipment?.id, certificate?.id, updateShipment, createShipment, showError]);

  const handleSentDateAction = useCallback(() => {
    handleShipmentAction('sent_by_regulator_at', isEditingSentDate, setIsEditingSentDate);
  }, [handleShipmentAction, isEditingSentDate]);

  const handleReceivedDateAction = useCallback(() => {
    handleShipmentAction('received_by_secretary_at', isEditingReceivedDate, setIsEditingReceivedDate);
  }, [handleShipmentAction, isEditingReceivedDate]);

  return (
    <fieldset className="mb-6">
      <FieldsetTitle
        title="Controle de impressão e recebimento"
        icon={Icons.ScheduleOutlined}
        hint="Registro de envio e recebimento do certificado impresso"
      />

      <Flex gap={16} wrap className="mt-6">
        <div className="flex-1 min-w-80">
          <Card size="small" className="h-full">
            <Flex vertical gap={16} className="h-full">
              <div>
                <Typography.Title level={5} className="!mb-2">
                  Enviado pela reguladora
                </Typography.Title>
                <Typography.Text type="secondary" className="text-sm">
                  Registro da data em que a reguladora enviou o certificado para a secretaria
                </Typography.Text>
              </div>
              
              <div className="flex-1">
                <Flex gap={16} align="end">
                  <Form.Item 
                    name="sent_by_regulator_at" 
                    className="!mb-0 flex-1"
                    rules={[{ required: true, message: 'Data de envio pela reguladora é obrigatória' }]}
                  >
                    <DatePicker 
                      placeholder="Selecione a data de envio"
                      format="DD/MM/YYYY"
                      className="w-full"
                      disabled={!isEditingSentDate}
                      disabledDate={disabledDate}
                    />
                  </Form.Item>
                  <Button
                    type="primary"
                    icon={isEditingSentDate ? <Icons.SaveOutlined /> : (certificate?.last_shipment?.sent_by_regulator_at ? <Icons.EditOutlined /> : <Icons.PlusOutlined />)}
                    loading={(isUpdatingShipment || isCreatingShipment) && isEditingSentDate}
                    disabled={isUpdatingShipment || isCreatingShipment}
                    onClick={handleSentDateAction}
                  >
                    {isEditingSentDate ? 'Salvar' : (certificate?.last_shipment?.sent_by_regulator_at ? 'Editar' : 'Adicionar')}
                  </Button>
                </Flex>
              </div>
            </Flex>
          </Card>
        </div>

        <div className="flex-1 min-w-80">
          <Card size="small" className="h-full">
            <Flex vertical gap={16} className="h-full">
              <div>
                <Typography.Title level={5} className="!mb-2">
                  Recebido pela secretaria
                </Typography.Title>
                <Typography.Text type="secondary" className="text-sm">
                  Registro da data em que a secretaria recebeu o certificado da reguladora
                </Typography.Text>
              </div>
              
              <div className="flex-1">
                <Flex gap={16} align="end">
                  <Form.Item 
                    name="received_by_secretary_at" 
                    className="!mb-0 flex-1"
                    rules={[{ required: true, message: 'Data de recebimento pela secretaria é obrigatória' }]}
                  >
                    <DatePicker 
                      placeholder="Selecione a data de recebimento"
                      format="DD/MM/YYYY"
                      className="w-full"
                      disabled={!isEditingReceivedDate}
                      disabledDate={disabledDate}
                    />
                  </Form.Item>
                  <Button
                    type="primary"
                    icon={isEditingReceivedDate ? <Icons.SaveOutlined /> : (certificate?.last_shipment?.received_by_secretary_at ? <Icons.EditOutlined /> : <Icons.PlusOutlined />)}
                    loading={(isUpdatingShipment || isCreatingShipment) && isEditingReceivedDate}
                    disabled={isUpdatingShipment || isCreatingShipment}
                    onClick={handleReceivedDateAction}
                  >
                    {isEditingReceivedDate ? 'Salvar' : (certificate?.last_shipment?.received_by_secretary_at ? 'Editar' : 'Adicionar')}
                  </Button>
                </Flex>
              </div>
            </Flex>
          </Card>
        </div>
      </Flex>
    </fieldset>
  );
};
